import { type IProjectBus, ProjectBusEvents, TOKEN_IProjectBus } from "@/services/ProjectBus";
import { TOKEN_IProjectWorldReader, type IProjectWorldReader } from "@/services/ProjectWorldReader";
import { TOKEN_IUserIdentity, type IUserIdentity } from "@/services/UserIdentity";
import { makeAutoObservable } from "mobx";
import { inject, injectable } from "tsyringe";
import { ReplayStore, TOKEN_ReplayStore } from "./ReplayStore";
import { TOKEN_IProjectSyncing, type IProjectSyncing } from "@/services/ProjectSyncing";
import { TOKEN_IWrapAuth0, type IWrapAuth0 } from "@/services/WrapAuth0";
import { AccessLevel } from "@utilitydelta/eventplanedb-client";
import { TOKEN_IEnvironment, type IEnvironment } from "@/services/IEnvironment";

export const TOKEN_ProjectAccessStore = Symbol("ProjectAccessStore");

@injectable()
export class ProjectAccessStore {
    get currentProjectId() {
        return this._projectId;
    }
    private _currentUserAccessLevel: AccessLevel | undefined = undefined;
    public get isInHistoryMode(): boolean {
        return this.replayStore.isInHistoryMode;
    }
    private _projectId: string | undefined = undefined;

    public get isUIReadOnly(): boolean {
        return this._currentUserAccessLevel === AccessLevel.Viewer || this.replayStore.isInHistoryMode;
    }

    public get hasAccessToProject(): boolean {
        return this._currentUserAccessLevel !== undefined;
    }

    public get isOwner(): boolean {
        return this._currentUserAccessLevel === AccessLevel.Owner;
    }

    constructor(
        @inject(TOKEN_IProjectBus) projectBus: IProjectBus,
        @inject(TOKEN_IEnvironment) private readonly environment: IEnvironment,
        @inject(TOKEN_IUserIdentity) private readonly userIdentity: IUserIdentity,
        @inject(TOKEN_IWrapAuth0) private readonly wrapAuth0: IWrapAuth0,
        @inject(TOKEN_IProjectWorldReader) private readonly projectWorldReader: IProjectWorldReader,
        @inject(TOKEN_ReplayStore) private readonly replayStore: ReplayStore,
        @inject(TOKEN_IProjectSyncing) private readonly projectSyncing: IProjectSyncing,
    ) {
        // Handle when the user access is changed
        projectBus.subscribe_projectionUpdated(this.onProjectionUpdated);

        // Handle when project is changed or loaded
        projectBus.subscribe_projectLoaded(this.onProjectLoaded);

        projectBus.subscribe_connectivityChanged(this.onConnectivityChanged);

        projectBus.subscribe_tabFocusChanged(this.onTabFocusChanged);

        projectBus.subscribe_authenticationChanged(this.onAuthenticationChanged);

        makeAutoObservable(this);
    }
    private onAuthenticationChanged = async (data: ProjectBusEvents['AUTHENTICATION_CHANGED']) => {
        if (!this._projectId) return;

        const identity = data.user?.sub ?? (await this.userIdentity.publicKey()).publicKeyHashed;
        const projectWorld = await this.projectWorldReader.mustGet(this._projectId);
        const projectUser = projectWorld.projectUsers.maybeGet(identity);
        this._currentUserAccessLevel = projectUser === undefined ? undefined : projectUser.accessLevel;
    }
    private onConnectivityChanged = async (data: ProjectBusEvents['CONNECTIVITY_CHANGED']) => {
        if (this._projectId === undefined || !data.isOnline) return;

        // We are now online again
        this.projectSyncing.readFromServer(this._projectId);
        this.projectSyncing.writeToServer(this._projectId);
    }

    private onTabFocusChanged = async (data: ProjectBusEvents['TAB_FOCUS_CHANGED']) => {
        if (this._projectId === undefined || !data.isFocused) return;
        if (!this.environment.isProd) return;
        this.projectSyncing.readFromServer(this._projectId);
    }

    private onProjectLoaded = async (data: ProjectBusEvents['PROJECT_LOADED']) => {
        const projectWorld = await this.projectWorldReader.mustGet(data.projectId);
        const identity = this.wrapAuth0.userIdHashed ?? (await this.userIdentity.publicKey()).publicKeyHashed;
        const projectUser = projectWorld.projectUsers.maybeGet(identity);

        this._projectId = data.projectId;
        this._currentUserAccessLevel = projectUser === undefined ? undefined : projectUser.accessLevel;
    }

    private onProjectionUpdated = async (data: ProjectBusEvents['PROJECTION_UPDATED']) => {
        if (data.mutationResult.kind !== 'ProvideAccess_ProjectionResult') return;
        if (this._projectId !== data.projectId) return; // Ensure we are in the correct project context

        const identity = this.wrapAuth0.userIdHashed ?? (await this.userIdentity.publicKey()).publicKeyHashed;
        if (data.mutationResult.projectUser.id !== identity) return; // Only update if the mutation is for the current user

        this._currentUserAccessLevel = data.mutationResult.projectUser.accessLevel;
    }
}